<template>
  <v-data-table fixed-header disable-pagination hide-default-footer :headers="headers" :items="items" no-data-text="Sem dados">
    <template v-slot:[`item.text`]="{ item }">
      {{ item.text }}
    </template>
    <template v-slot:[`item.count`]="{ item }">
      {{ item['count'] | localeString }}
    </template>
    <template v-slot:[`item.representation`]="{ item }"> {{ locale(item['representation'], 2) | localeString(1) | percent }} </template>
    <template v-slot:[`item.revenue`]="{ item }"> {{ locale(item['revenue'], 2) | localeString(2) | currency }} </template>
    <template v-slot:[`item.averageTicket`]="{ item }"> {{ locale(item['averageTicket'], 2) | hifen | localeString(2) | currency }} </template>
    <template v-slot:[`item.coupons`]="{ item }">{{ item['coupons'] | localeString }} </template>
  </v-data-table>
</template>

<script>
import { localeString } from '@/config/helpers.js'

export default {
  name: 'ProfileResultsTable',
  props: {
    metrics: {
      type: Object,
      default: () => {},
    },
    baseMetric: {
      type: String,
      default: 'received',
    },
  },
  data: () => ({ locale: localeString }),
  computed: {
    baseMetricAttributes() {
      switch (this.baseMetric) {
        case 'received':
          return {
            percentageColumnName: 'Percentual dos que receberam',
            base: 'received',
            groups: [
              {
                label: 'Receberam e compraram',
                key: 'received_and_bought',
              },
              {
                label: 'Receberam e compraram produtos divulgados',
                key: 'received_with_products',
              },
              {
                label: 'Receberam e compraram apenas produtos divulgados',
                key: 'received_with_only_products',
              },
            ],
          }
        case 'opened':
          return {
            percentageColumnName: 'Percentual dos que abriram',
            base: 'opened',
            groups: [
              {
                label: 'Abriram e compraram',
                key: 'opened_and_bought',
              },
              {
                label: 'Abriram e compraram produtos divulgados',
                key: 'opened_with_products',
              },
              {
                label: 'Abriram e compraram apenas produtos divulgados',
                key: 'opened_with_only_products',
              },
            ],
          }
        case 'sales':
          return {
            percentageColumnName: 'Percentual dos que compraram',
            base: 'total',
            groups: [
              {
                label: 'Compraram produtos divulgados',
                key: 'with_sales',
              },
              {
                label: 'Compraram apenas produtos divulgados',
                key: 'with_only_sales',
              },
            ],
          }
        case 'activatable':
          return {
            percentageColumnName: 'Percentual dos que compraram',
            base: 'total',
            groups: [
              {
                label: 'Ativaram ofertas',
                key: 'with_activated_sales',
              },
              {
                label: 'Compraram produtos divulgados',
                key: 'with_sales',
              },
              {
                label: 'Compraram apenas produtos divulgados',
                key: 'with_only_sales',
              },
            ],
          }
        case 'highlight':
          return {
            percentageColumnName: 'Percentual dos que visualizaram',
            base: 'highlight',
            groups: [
              {
                label: 'Visualizaram e compraram',
                key: 'viewed_and_bought',
              },
              {
                label: 'Visualizaram e compraram produtos divulgados',
                key: 'viewed_with_products',
              },
              {
                label: 'Visualizaram e compraram apenas produtos divulgados',
                key: 'viewed_with_only_products',
              },
            ],
          }
        default:
          return {}
      }
    },
    headers() {
      return [
        {
          text: '',
          value: 'text',
          sortable: false,
        },
        {
          text: 'Clientes',
          value: 'count',
          align: 'left',
        },
        {
          text: this.baseMetricAttributes.percentageColumnName,
          value: 'representation',
          align: 'left',
        },
        {
          text: 'Faturamento',
          value: 'revenue',
          align: 'left',
        },
        {
          text: 'Ticket médio',
          value: 'averageTicket',
          align: 'left',
        },
        {
          text: 'Cupons',
          value: 'coupons',
          align: 'left',
        },
      ]
    },
    items() {
      if (!this.metrics?.clients_metrics) return []

      const { clients_metrics } = this.metrics
      const { groups, base } = this.baseMetricAttributes

      const data = groups.map(({ label, key, quickActionsProps }) => {
        const groupMetrics = clients_metrics[key] || {}
        const baseCount = clients_metrics[base]?.count || 1

        const representation = this.baseMetric === 'highlight' ? groupMetrics.representation || 0 : ((groupMetrics.count || 0) / baseCount) * 100

        return {
          text: label,
          count: groupMetrics.count,
          representation,
          revenue: groupMetrics.revenue,
          averageTicket: groupMetrics.average_ticket,
          coupons: groupMetrics.tickets,
          quickActionsProps,
        }
      })

      const emptyArrayCheck = data.every(
        (item) => item.count === 0 && item.representation === 0 && item.revenue === 0 && item.averageTicket === 0 && item.coupons === 0
      )

      return emptyArrayCheck ? [] : data
    },
  },
  methods: {},
}
</script>

<style lang="scss" scoped>
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
  text-align: left;
  border-left: 1px solid #eee;
}
::v-deep thead th {
  border-bottom: 1px solid #eee !important;
}

::v-deep .v-data-table-header tr,
::v-deep .v-data-table-header th {
  background: #fff !important;
  border-top: 1px solid #fff !important;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 12px;
    line-height: 1;
    border-left: 1px solid #eee;
  }
}
::v-deep tbody {
  td:first-child {
    border-left: none;
  }
}
::v-deep th:first-child {
  border-left: none;
}
</style>
